import React, { useState, useEffect, useRef } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

export default function ForgotPasswordForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    emailRef?.current?.focus();
  }, []);

  const validateEmail = (val) => {
    if (!val) return "Email address is required";
    if (!/\S+@\S+\.\S+/?.test(val)) return "Please enter a valid email address";
    return "";
  };

  const handleChange = (e) => {
    setEmail(e?.target?.value);
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) { setError(validationError); return; }
    setIsLoading(true);
    setError("");

    try {
      await api.post('/auth/forgot-password', { email });
      setIsLoading(false);
      onSuccess?.(email);
    } catch (err) {
      setIsLoading(false);
      const msg = err.response?.data?.message || "";
      if (msg?.toLowerCase()?.includes("not found") || msg?.toLowerCase()?.includes("user")) {
        setError("No account found with this email address. Please check and try again.");
      } else {
        setError(msg || "Failed to send reset email. Please try again.");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <div className="space-y-1.5">
        <label htmlFor="reset-email" className="text-sm font-medium text-slate-300 font-caption">
          Email address
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
            <Icon name="Mail" size={16} />
          </div>
          <input
            ref={emailRef}
            id="reset-email"
            type="email"
            value={email}
            onChange={handleChange}
            onBlur={() => { if (email) setError(validateEmail(email)); }}
            placeholder="you@gymname.com"
            autoComplete="email"
            className={`w-full h-11 pl-10 pr-4 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all duration-200 ${
              error ? "border-red-500/60 bg-red-500/5" : "border-white/10 focus:border-teal-500/50"
            }`}
          />
        </div>
        {error && (
          <div className="flex items-start gap-1.5">
            <Icon name="AlertCircle" size={13} color="#f87171" className="mt-0.5 shrink-0" />
            <p className="text-xs text-red-400 font-caption leading-relaxed">{error}</p>
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.98] text-white font-semibold text-sm font-caption transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-teal-900/30"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Sending reset link...
          </>
        ) : (
          <>
            Send Reset Link
            <Icon name="Send" size={16} />
          </>
        )}
      </button>
    </form>
  );
}
